import { PermissionLevel } from '../types';

// 权限级别的数值映射
const PERMISSION_VALUES: Record<PermissionLevel, number> = {
  basic: 1,
  intermediate: 2,
  advanced: 3,
  uploader: 4,
  admin: 5,
  super_admin: 6,
};

// 权限级别的中文标签
export const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  basic: '初级',
  intermediate: '中级',
  advanced: '高级',
  uploader: '上传管理员',
  admin: '管理员',
  super_admin: '超级管理员',
};

// 权限级别的颜色主题
export const PERMISSION_COLORS: Record<PermissionLevel, string> = {
  basic: 'bg-green-100 text-green-800',
  intermediate: 'bg-blue-100 text-blue-800',
  advanced: 'bg-purple-100 text-purple-800',
  uploader: 'bg-orange-100 text-orange-800',
  admin: 'bg-red-100 text-red-800',
  super_admin: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
};

/**
 * 检查用户是否有足够的权限
 * @param userRole 用户角色
 * @param requiredLevel 所需权限级别
 * @returns 是否有权限
 */
export function hasPermission(
  userRole: PermissionLevel,
  requiredLevel: PermissionLevel
): boolean {
  return PERMISSION_VALUES[userRole] >= PERMISSION_VALUES[requiredLevel];
}

/**
 * 获取权限级别的数值
 * @param level 权限级别
 * @returns 数值
 */
export function getPermissionValue(level: PermissionLevel): number {
  return PERMISSION_VALUES[level];
}

/**
 * 获取用户可访问的权限级别列表
 * @param userRole 用户角色
 * @returns 可访问的权限级别数组
 */
export function getAccessibleLevels(userRole: PermissionLevel): PermissionLevel[] {
  const userValue = PERMISSION_VALUES[userRole];
  return (Object.keys(PERMISSION_VALUES) as PermissionLevel[]).filter(
    level => PERMISSION_VALUES[level] <= userValue
  );
}

/**
 * 检查用户是否可以管理其他用户的权限
 * @param managerRole 管理者角色
 * @param targetRole 目标用户角色
 * @param managerCanAccessAllTenants 管理者是否有跨租户权限
 * @param targetCanAccessAllTenants 目标用户是否有跨租户权限
 * @returns 是否可以管理
 */
export function canManageUser(
  managerRole: PermissionLevel,
  targetRole: PermissionLevel,
  managerCanAccessAllTenants?: boolean,
  targetCanAccessAllTenants?: boolean
): boolean {
  const managerIsSuperAdmin = isSuperAdmin(managerRole, managerCanAccessAllTenants);
  const targetIsSuperAdmin = isSuperAdmin(targetRole, targetCanAccessAllTenants);
  
  // 超级管理员可以管理所有用户，除了其他超级管理员
  if (managerIsSuperAdmin) {
    return !targetIsSuperAdmin;
  }
  
  // 普通管理员只能管理低于自己权限的用户（不能管理其他管理员）
  if (managerRole === 'admin') {
    return PERMISSION_VALUES[managerRole] > PERMISSION_VALUES[targetRole];
  }
  
  return false;
}

/**
 * 获取权限级别的描述
 * @param level 权限级别
 * @returns 描述文本
 */
export function getPermissionDescription(level: PermissionLevel): string {
  const descriptions: Record<PermissionLevel, string> = {
    basic: '可以访问基础文件和公共内容',
    intermediate: '可以访问中级文件和部分高级内容，允许上传文件',
    advanced: '可以访问高级文件和大部分受限内容，管理自己的文件',
    uploader: '完整的文件和分类管理权限，不能管理用户权限',
    admin: '拥有租户完全访问权限，可以管理租户内所有内容和用户',
    super_admin: '拥有全系统访问权限，可以管理所有租户的内容和用户',
  };
  return descriptions[level];
}

/**
 * 根据文件类型推荐默认权限级别
 * @param fileType 文件类型
 * @returns 推荐的权限级别
 */
export function getRecommendedPermissionLevel(fileType: string): PermissionLevel {
  const recommendations: Record<string, PermissionLevel> = {
    // 基础文件类型
    image: 'basic',
    document: 'basic',
    
    // 中级文件类型
    pdf: 'intermediate',
    spreadsheet: 'intermediate',
    presentation: 'intermediate',
    
    // 高级文件类型
    video: 'advanced',
    audio: 'advanced',
    code: 'advanced',
    
    // 其他
    other: 'basic',
  };
  
  return recommendations[fileType] || 'basic';
}

/**
 * 生成公共访问令牌
 * @returns 随机令牌字符串
 */
export function generatePublicAccessToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * 检查公共访问是否有效
 * @param expiresAt 过期时间
 * @returns 是否有效
 */
export function isPublicAccessValid(expiresAt?: string): boolean {
  if (!expiresAt) return true; // 没有过期时间表示永久有效
  return new Date(expiresAt) > new Date();
}

/**
 * 检查用户是否可以上传文件
 * @param userRole 用户角色
 * @returns 是否可以上传文件
 */
export function canUploadFiles(userRole: PermissionLevel): boolean {
  return PERMISSION_VALUES[userRole] >= PERMISSION_VALUES['intermediate'];
}

/**
 * 检查用户是否可以管理文件和分类（但不能管理用户）
 * @param userRole 用户角色
 * @returns 是否可以管理文件
 */
export function canManageFiles(userRole: PermissionLevel): boolean {
  return PERMISSION_VALUES[userRole] >= PERMISSION_VALUES['uploader'];
}

/**
 * 检查用户是否可以访问管理员面板
 * @param userRole 用户角色
 * @returns 是否可以访问管理面板
 */
export function canAccessAdminPanel(userRole: PermissionLevel): boolean {
  return PERMISSION_VALUES[userRole] >= PERMISSION_VALUES['uploader'];
}

/**
 * 检查用户是否可以管理用户和权限
 * @param userRole 用户角色
 * @returns 是否可以管理用户
 */
export function canManageUsers(userRole: PermissionLevel): boolean {
  return userRole === 'admin' || userRole === 'super_admin';
}

/**
 * 检查用户是否有管理员权限（包括 admin 和 super_admin）
 * 用于判断用户是否可以执行管理操作（如修改可见性、管理分类等）
 * @param userRole 用户角色
 * @returns 是否有管理员权限
 */
export function isAdmin(userRole: PermissionLevel): boolean {
  return userRole === 'admin' || userRole === 'super_admin';
}

/**
 * 检查用户是否是超级管理员
 * 超级管理员的判断标准：
 * 1. role === 'super_admin'
 * 2. 或者 role === 'admin' 且 can_access_all_tenants === true
 * 
 * @param userRole 用户角色
 * @param canAccessAllTenants 是否有跨租户权限（可选）
 * @returns 是否是超级管理员
 */
export function isSuperAdmin(userRole: PermissionLevel, canAccessAllTenants?: boolean): boolean {
  if (userRole === 'super_admin') return true;
  if (userRole === 'admin' && canAccessAllTenants === true) return true;
  return false;
}
